// studioIcons.tsx — SVG icon set + per-stem visual config for the Studio
// console redesign (ported from the Claude Design handoff). Stem ids match the
// app's StemName ('other' is the design's "Solo & Lead"). The per-stem colors
// flow through the region, CTA, processing bar and result card.
import type { CSSProperties, ReactNode } from 'react';
import type { StemName } from '../lib/studioEngine';

interface IconProps {
  size?: number;
  sw?: number;
  fill?: string;
  className?: string;
  style?: CSSProperties;
  d?: string;
  children?: ReactNode;
}

function I({ d, size = 18, sw = 1.7, fill, children, ...p }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill || 'none'}
      stroke={fill ? 'none' : 'currentColor'}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      {d ? <path d={d} /> : children}
    </svg>
  );
}

type IconFn = (p?: Omit<IconProps, 'd' | 'children'>) => ReactNode;

export const Icons: Record<string, IconFn> = {
  upload: (p) => (
    <I {...p}>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" />
    </I>
  ),
  file: (p) => (
    <I {...p}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </I>
  ),
  music: (p) => (
    <I {...p}>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6" cy="18" r="2.6" />
      <circle cx="16" cy="16" r="2.6" />
    </I>
  ),
  play: (p) => <I {...p} fill="currentColor" d="M8 5.5v13l11-6.5z" />,
  pause: (p) => (
    <I {...p} fill="currentColor">
      <rect x="7" y="5" width="3.4" height="14" rx="1" />
      <rect x="13.6" y="5" width="3.4" height="14" rx="1" />
    </I>
  ),
  check: (p) => <I {...p} sw={2.4} d="M4 12.5 9.5 18 20 6.5" />,
  x: (p) => <I {...p} d="M6 6l12 12M18 6 6 18" />,
  download: (p) => (
    <I {...p}>
      <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5" />
      <path d="M5 19h14" />
    </I>
  ),
  scissors: (p) => (
    <I {...p}>
      <circle cx="6" cy="6" r="2.6" />
      <circle cx="6" cy="18" r="2.6" />
      <path d="M8.2 7.6 20 18M20 6 8.2 16.4" />
    </I>
  ),
  save: (p) => (
    <I {...p}>
      <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
      <path d="M8 4v5h7M8 21v-7h8v7" />
    </I>
  ),
  plus: (p) => <I {...p} d="M12 5v14M5 12h14" />,
  spark: (p) => <I {...p} d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.5 2.5M15.2 15.2l2.5 2.5M17.7 6.3l-2.5 2.5M8.8 15.2l-2.5 2.5" />,
  link: (p) => (
    <I {...p}>
      <path d="M9 15l6-6" />
      <path d="M11 6l1-1a4 4 0 0 1 6 6l-1 1M13 18l-1 1a4 4 0 0 1-6-6l1-1" />
    </I>
  ),
  // The YouTube silhouette drawn as one of ours: outlined body in currentColor
  // at the shared stroke weight, so it sits in a row of icons rather than
  // shouting over them. Deliberately NOT the red brand mark — that belongs on
  // the dashboard's "Riffloop for Chrome" promo card, where it's an ad; in a
  // source picker it just reads as a stray logo. Only the play head is filled,
  // which is what makes the shape legible at 15px.
  youtube: (p) => (
    <I {...p}>
      <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="4.2" />
      <path d="M10.4 9.7v4.6l4.1-2.3z" fill="currentColor" stroke="none" />
    </I>
  ),
  info: (p) => (
    <I {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </I>
  ),
  loop: (p) => (
    <I {...p}>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </I>
  ),
  // Same pencil mark as the extension's inline-rename control.
  pencil: (p) => (
    <I {...p} sw={2}>
      <path d="M14 3l7 7L8 23H1v-7z" />
      <path d="M14 3l3-3 7 7-3 3" />
    </I>
  ),
};

// Per-stem crafted mark (color passed in so it can render filled-on-color or
// tinted-on-surface).
export const stemMark: Record<StemName, (c: string) => ReactNode> = {
  vocals: (c) => (
    <I size={20} fill={undefined} style={{ stroke: c }}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" />
    </I>
  ),
  drums: (c) => (
    <I size={20} style={{ stroke: c }}>
      <path d="M3 10.5c0 1.7 4 3 9 3s9-1.3 9-3-4-3-9-3-9 1.3-9 3Z" />
      <path d="M3 10.5v3c0 1.7 4 3 9 3s9-1.3 9-3v-3" />
      <path d="M8 13.5 5.5 20M16 13.5 18.5 20" />
    </I>
  ),
  bass: (c) => (
    <I size={20} style={{ stroke: c }}>
      <path d="M4 12h2l1.5-5 2 11 2-13 2 16 1.5-9H20" />
    </I>
  ),
  piano: (c) => (
    <I size={20} style={{ stroke: c }}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 14h18M8 4v10M13 4v10M18 4v10" />
    </I>
  ),
  guitar: (c) => (
    <I size={20} style={{ stroke: c }}>
      <path d="M14 4l3 3M15.5 5.5 19 4l1 1-1.5 3.5" />
      <path d="M14.5 8.5 11 12" />
      <circle cx="8" cy="15" r="5" />
      <circle cx="8" cy="15" r="1.6" />
    </I>
  ),
  other: (c) => (
    <I size={20} style={{ stroke: c }}>
      <path d="M12 3l2 5 5 .5-3.8 3.4L16.5 17 12 14.3 7.5 17l1.3-5.1L5 8.5 10 8z" />
    </I>
  ),
};

export interface StemUI {
  id: StemName;
  name: string;
  desc: string;
  color: string;
  verb: string;
}

// Display order + color coding. 'other' = Solo & Lead.
export const STEM_UI: StemUI[] = [
  { id: 'vocals', name: 'Vocals', desc: 'lead & harmony', color: '#a78bfa', verb: 'Isolate vocals' },
  { id: 'drums', name: 'Drums', desc: 'kit & percussion', color: '#fb923c', verb: 'Isolate drums' },
  { id: 'bass', name: 'Bass', desc: 'low end', color: '#38bdf8', verb: 'Isolate bass' },
  { id: 'piano', name: 'Piano', desc: 'keys', color: '#34d399', verb: 'Isolate piano' },
  { id: 'guitar', name: 'Guitar', desc: 'electric & ac.', color: '#fbbf24', verb: 'Isolate guitar' },
  { id: 'other', name: 'Solo & Lead', desc: 'melodic lead', color: '#f472b6', verb: 'Isolate lead' },
];

export const STEM_COLOR: Record<StemName, string> = STEM_UI.reduce(
  (acc, s) => ((acc[s.id] = s.color), acc),
  {} as Record<StemName, string>,
);
